/**
 * The e-mails of the game's accounts (services/email.ts) in the three languages of the game: the subject, a plain text
 * body and a minimal HTML body. A message holds at most one link (the account link of the site, public/link/) and the
 * nickname (data, never translated); never a password. Addresses other than the recipient's appear partly hidden.
 */
import type { MessageLanguage } from '../../shared/protocol/model';

export type EmailKind = 'verify' | 'address_in_use' | 'reset' | 'email_changing' | 'password_changed';

export interface EmailVars {
  /** The account's nickname (data). */
  nickname?: string;
  /** The account link (verify, reset). */
  link?: string;
  /** How long the link works, in hours. */
  hours?: number;
  /** The new address, partly hidden (email_changing). */
  newEmail?: string;
  /** The reset also cancelled a pending address change (password_changed). */
  changeCancelled?: boolean;
  /** The reset also removed the account's Play Games link (password_changed). */
  playGamesUnlinked?: boolean;
}

export interface RenderedEmail {
  subject: string;
  text: string;
  html: string;
}

type Texts = { subject: string; lines: (v: EmailVars) => string[]; button?: string };

const T: Record<MessageLanguage, Record<EmailKind, Texts>> = {
  uk: {
    verify: {
      subject: 'FWP: підтвердьте e-mail',
      button: 'Підтвердити e-mail',
      lines: (v) => [`Вітаємо, ${v.nickname}!`, 'Щоб входити в FWP за цією адресою й відновлювати пароль, підтвердьте її за посиланням:', v.link!, `Посилання діє ${v.hours} год.`, 'Якщо ви не реєструвалися в FWP, просто проігноруйте цей лист.'],
    },
    address_in_use: {
      subject: 'FWP: спроба використати вашу адресу',
      lines: () => ['Хтось щойно вказав цю адресу для іншого акаунта FWP. Ваша адреса вже належить вашому акаунту, тож нічого не змінилося.', 'Якщо це були ви й забули пароль, скористайтеся кнопкою «Забули пароль?» на екрані входу в гру.', 'Якщо ні — просто проігноруйте цей лист.'],
    },
    reset: {
      subject: 'FWP: відновлення пароля',
      button: 'Задати новий пароль',
      lines: (v) => [`Вітаємо, ${v.nickname}!`, 'Ви попросили змінити пароль акаунта FWP. Задайте новий пароль за посиланням:', v.link!, `Посилання діє ${v.hours} год і працює один раз. Після зміни пароля всі пристрої вийдуть з акаунта.`, 'Якщо ви цього не просили, проігноруйте лист: пароль залишиться тим самим.'],
    },
    email_changing: {
      subject: 'FWP: зміна e-mail акаунта',
      lines: (v) => [`Вітаємо, ${v.nickname}!`, `Для акаунта FWP попросили нову адресу для входу: ${v.newEmail}. Вона запрацює, коли її підтвердять за посиланням з листа на неї; доти вхід працює з цією адресою.`, 'Якщо це були не ви, одразу скористайтеся «Забули пароль?» на екрані входу в гру: новий пароль скасує зміну адреси й завершить усі сесії акаунта.'],
    },
    password_changed: {
      subject: 'FWP: пароль змінено',
      lines: (v) => [`Вітаємо, ${v.nickname}!`, 'Пароль акаунта FWP щойно змінено за посиланням з листа. Усі пристрої вийшли з акаунта.', ...(v.changeCancelled ? ['Зміну адреси входу, що чекала на підтвердження, скасовано.'] : []), ...(v.playGamesUnlinked ? ['Прив\'язку Google Play Games знято: за потреби прив\'яжіть її знову в грі.'] : []), 'Якщо це були не ви, скористайтеся «Забули пароль?» на екрані входу й напишіть у підтримку.'],
    },
  },
  en: {
    verify: {
      subject: 'FWP: confirm your e-mail',
      button: 'Confirm e-mail',
      lines: (v) => [`Hello, ${v.nickname}!`, 'To sign in to FWP with this address and to be able to reset your password, confirm it with this link:', v.link!, `The link works for ${v.hours} h.`, 'If you did not sign up for FWP, just ignore this e-mail.'],
    },
    address_in_use: {
      subject: 'FWP: somebody tried to use your address',
      lines: () => ['Somebody just entered this address for another FWP account. Your address still belongs to your account: nothing has changed.', 'If it was you and you forgot your password, use "Forgot password?" on the sign-in screen of the game.', 'Otherwise just ignore this e-mail.'],
    },
    reset: {
      subject: 'FWP: password reset',
      button: 'Set a new password',
      lines: (v) => [`Hello, ${v.nickname}!`, 'You asked to change the password of your FWP account. Set a new password with this link:', v.link!, `The link works for ${v.hours} h and only once. After the change every device is signed out.`, 'If you did not ask for it, ignore this e-mail: your password stays the same.'],
    },
    email_changing: {
      subject: 'FWP: sign-in e-mail change',
      lines: (v) => [`Hello, ${v.nickname}!`, `A new sign-in address was requested for your FWP account: ${v.newEmail}. It starts working once it is confirmed with the link sent to it; until then you sign in with this address.`, 'If it was not you, use "Forgot password?" on the sign-in screen of the game right away: the new password cancels the address change and signs every device out.'],
    },
    password_changed: {
      subject: 'FWP: password changed',
      lines: (v) => [`Hello, ${v.nickname}!`, 'The password of your FWP account was just changed with an e-mail link. Every device was signed out.', ...(v.changeCancelled ? ['The change of the sign-in address that waited for its confirmation was cancelled.'] : []), ...(v.playGamesUnlinked ? ['The Google Play Games link was removed: link it again in the game if you need it.'] : []), 'If it was not you, use "Forgot password?" on the sign-in screen and contact support.'],
    },
  },
  ru: {
    verify: {
      subject: 'FWP: подтвердите e-mail',
      button: 'Подтвердить e-mail',
      lines: (v) => [`Здравствуйте, ${v.nickname}!`, 'Чтобы входить в FWP по этому адресу и восстанавливать пароль, подтвердите его по ссылке:', v.link!, `Ссылка действует ${v.hours} ч.`, 'Если вы не регистрировались в FWP, просто проигнорируйте это письмо.'],
    },
    address_in_use: {
      subject: 'FWP: попытка использовать ваш адрес',
      lines: () => ['Кто-то только что указал этот адрес для другого аккаунта FWP. Ваш адрес по-прежнему принадлежит вашему аккаунту: ничего не изменилось.', 'Если это были вы и вы забыли пароль, нажмите «Забыли пароль?» на экране входа в игру.', 'Если нет — просто проигнорируйте это письмо.'],
    },
    reset: {
      subject: 'FWP: восстановление пароля',
      button: 'Задать новый пароль',
      lines: (v) => [`Здравствуйте, ${v.nickname}!`, 'Вы попросили сменить пароль аккаунта FWP. Задайте новый пароль по ссылке:', v.link!, `Ссылка действует ${v.hours} ч и работает один раз. После смены пароля все устройства выйдут из аккаунта.`, 'Если вы этого не просили, проигнорируйте письмо: пароль останется прежним.'],
    },
    email_changing: {
      subject: 'FWP: смена e-mail аккаунта',
      lines: (v) => [`Здравствуйте, ${v.nickname}!`, `Для аккаунта FWP запросили новый адрес для входа: ${v.newEmail}. Он заработает, когда его подтвердят по ссылке из письма на него; до тех пор вход работает с этим адресом.`, 'Если это были не вы, сразу нажмите «Забыли пароль?» на экране входа в игру: новый пароль отменит смену адреса и завершит все сеансы аккаунта.'],
    },
    password_changed: {
      subject: 'FWP: пароль изменён',
      lines: (v) => [`Здравствуйте, ${v.nickname}!`, 'Пароль аккаунта FWP только что изменён по ссылке из письма. Все устройства вышли из аккаунта.', ...(v.changeCancelled ? ['Смена адреса для входа, ожидавшая подтверждения, отменена.'] : []), ...(v.playGamesUnlinked ? ['Привязка Google Play Games снята: при необходимости привяжите её снова в игре.'] : []), 'Если это были не вы, нажмите «Забыли пароль?» на экране входа и напишите в поддержку.'],
    },
  },
};

const escapeHtml = (text: string): string => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** The message of a kind in a language (uk when the language is unknown). */
export function renderEmail(kind: EmailKind, lang: MessageLanguage | undefined, vars: EmailVars): RenderedEmail {
  const t = (T[lang ?? 'uk'] ?? T.uk)[kind];
  const lines = t.lines({ nickname: vars.nickname ?? 'FWP', hours: vars.hours ?? 1, ...vars });
  const text = `${lines.join('\n\n')}\n\n— FWP\n`;
  const body = lines
    .map((line) =>
      line === vars.link && t.button
        ? `<p><a href="${escapeHtml(line)}" style="display:inline-block;padding:10px 18px;background:#338000;color:#fff;text-decoration:none;border-radius:6px;font-weight:bold">${escapeHtml(t.button)}</a></p><p style="font-size:12px;color:#6b5330">${escapeHtml(line)}</p>`
        : `<p>${escapeHtml(line)}</p>`,
    )
    .join('');
  const html = `<!doctype html><html><body style="font:15px/1.5 Arial,Helvetica,sans-serif;color:#3b2508">${body}<p>— FWP</p></body></html>`;
  return { subject: t.subject, text, html };
}
